import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import storeService from '../../services/storeService';
import userService from '../../services/userService';
import shiftService from '../../services/shiftService';
import { Store, User, UserWorkScheduleEntry } from '../../types';

const DAY_LABELS: { label: string; short: string; value: number }[] = [
  { label: 'Monday', short: 'Mon', value: 1 },
  { label: 'Tuesday', short: 'Tue', value: 2 },
  { label: 'Wednesday', short: 'Wed', value: 3 },
  { label: 'Thursday', short: 'Thu', value: 4 },
  { label: 'Friday', short: 'Fri', value: 5 },
  { label: 'Saturday', short: 'Sat', value: 6 },
  { label: 'Sunday', short: 'Sun', value: 7 },
];

const WorkTimingsPage: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [schedule, setSchedule] = useState<UserWorkScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [storeRes, shiftRes] = await Promise.all([
          storeService.getStores({ limit: 1000 }),
          shiftService.getShifts({ limit: 200 }),
        ]);
        setStores(storeRes.stores || []);
        setShifts(shiftRes.shifts || []);
      } catch (err: any) {
        console.error('Failed to load stores/shifts', err);
        setError('Failed to load stores or shifts');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const loadUsersForStore = async (storeId: string) => {
    try {
      setSchedule([]);
      setSelectedUser(null);
      setSelectedUserId('');

      const params: any = { limit: 1000, is_active: true };
      if (storeId !== 'all') {
        params.store_id = storeId;
      }
      const res = await userService.getUsers(params);
      setUsers(res.users || []);
    } catch (err: any) {
      console.error('Failed to load users', err);
      setError('Failed to load users');
    }
  };

  const loadScheduleForUser = async (user: User) => {
    try {
      setScheduleLoading(true);
      setError(null);
      const result = await userService.getUserWorkSchedule(user.id);
      const map = new Map<number, UserWorkScheduleEntry>();
      result.schedule.forEach((entry) => map.set(entry.day_of_week, entry));

      const full: UserWorkScheduleEntry[] = DAY_LABELS.map((d) => {
        const existing = map.get(d.value);
        return (
          existing || {
            user_id: user.id,
            day_of_week: d.value,
            is_week_off: d.value === 7, // default Sunday off
            shift_id: user.shift_id || null,
            shift: null,
          }
        );
      });

      setSchedule(full);
    } catch (err: any) {
      console.error('Failed to load work schedule', err);
      setError(err?.message || 'Failed to load work schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleStoreChange = async (storeId: string) => {
    setSelectedStoreId(storeId);
    await loadUsersForStore(storeId);
  };

  const handleUserChange = async (userId: string) => {
    setSelectedUserId(userId);
    const user = users.find((u) => u.id === userId) || null;
    setSelectedUser(user);
    if (user) {
      await loadScheduleForUser(user);
    } else {
      setSchedule([]);
    }
  };

  const updateScheduleEntry = (index: number, patch: Partial<UserWorkScheduleEntry>) => {
    setSchedule((prev) =>
      prev.map((entry, idx) => (idx === index ? { ...entry, ...patch } : entry))
    );
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    try {
      setScheduleLoading(true);
      const payload = schedule.map((entry) => ({
        day_of_week: entry.day_of_week,
        is_week_off: entry.is_week_off,
        shift_id: entry.is_week_off ? null : entry.shift_id || null,
      }));
      await userService.updateUserWorkSchedule(selectedUser.id, payload);
    } catch (err: any) {
      console.error('Failed to save work schedule', err);
      setError(err?.message || 'Failed to save work schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccessTime sx={{ color: '#081122' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#081122' }}>
          Work Timings
        </Typography>
      </Box>

      {error && (
        <Box mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select
                label="Store"
                value={selectedStoreId}
                onChange={(e) => handleStoreChange(e.target.value as string)}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} ({s.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small" disabled={users.length === 0}>
              <InputLabel>User</InputLabel>
              <Select
                label="User"
                value={selectedUserId}
                onChange={(e) => handleUserChange(e.target.value as string)}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.employee_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : !selectedUser ? (
          <Typography variant="body2" sx={{ color: '#666' }}>
            Select a store and user to configure work timings.
          </Typography>
        ) : scheduleLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {selectedUser.first_name} {selectedUser.last_name} ({selectedUser.employee_id})
              </Typography>
            </Box>
            <Grid container sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
              <Grid item xs={4}>
                Day
              </Grid>
              <Grid item xs={3}>
                Week Off
              </Grid>
              <Grid item xs={5}>
                Shift
              </Grid>
            </Grid>
            {schedule.map((entry, index) => {
              const meta = DAY_LABELS.find((d) => d.value === entry.day_of_week)!;
              return (
                <Grid
                  container
                  key={entry.day_of_week}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Grid item xs={4}>
                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>{meta.label}</Typography>
                      {entry.is_week_off && (
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          Every {meta.label} will be a week off
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Checkbox
                      checked={entry.is_week_off}
                      onChange={(e) =>
                        updateScheduleEntry(index, {
                          is_week_off: e.target.checked,
                          shift_id: e.target.checked ? null : entry.shift_id,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <FormControl fullWidth size="small" disabled={entry.is_week_off}>
                      <Select
                        value={entry.shift_id || ''}
                        displayEmpty
                        onChange={(e) =>
                          updateScheduleEntry(index, {
                            shift_id: (e.target.value as string) || null,
                          })
                        }
                        renderValue={(selected) => {
                          if (!selected) return 'Select Shift';
                          const shift = shifts.find((s) => s.id === selected);
                          return shift ? shift.name : 'Select Shift';
                        }}
                      >
                        <MenuItem value="">
                          <em>Select Shift</em>
                        </MenuItem>
                        {shifts.map((shift) => (
                          <MenuItem key={shift.id} value={shift.id}>
                            {shift.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              );
            })}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={() => selectedUser && loadScheduleForUser(selectedUser)}>
                Reset
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={scheduleLoading}>
                Save Changes
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default WorkTimingsPage;


